import React, { useEffect, useState, createContext, useContext, type ReactNode } from 'react';
type Language = 'EN' | 'FR';
interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (en: string, fr: string) => string;
}
const STORAGE_KEY = 'bciti-lang';
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
function getInitialLang(): Language {
  // French is the default language; a stored choice always wins.
  if (typeof window === 'undefined') return 'FR';
  // A language in the URL (?lang=en / ?lang=fr) wins so shared and crawled links open in the right language.
  const param = new URLSearchParams(window.location.search).get('lang');
  if (param === 'en') return 'EN';
  if (param === 'fr') return 'FR';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'FR' || stored === 'EN' ? stored : 'FR';
}
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [lang, setLangState] = useState<Language>(getInitialLang);
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore write failures (e.g. private mode)
    }
  }, [lang]);
  const setLang = (next: Language) => setLangState(next);
  const t = (en: string, fr: string) => lang === 'FR' ? fr : en;
  return <LanguageContext.Provider value={{
    lang,
    setLang,
    t
  }} data-id="element-4069" data-mp="1d8ooa0fya4mh">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}